import { useMemo } from 'react';
import { getBrandConfig, BrandConfig } from '../config/brandConfig';

// Thème de base commun à toutes les marques
const baseTheme = {
  // Typography
  fonts: {
    regular: "Nunito-Regular",
    bold: "Nunito-Bold",
    extraBold: "Nunito-ExtraBold",
    black: "Nunito-Black",
    poppins: "Poppins",
    poppinsLight: "Poppins-Light",
    buffalo: "Buffalo-Regular"
  },
  
  // Responsive font sizes
  fontSizes: {
    content: { base: "12px", md: "12px", lg: "16px" },
    title: { base: "20px", md: "30px", lg: "40px" },
    hugeTitle: { base: "60px", md: "80px", lg: "110px" }
  },
  
  // Spacing
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    xxl: "48px"
  },
  
  // Border radius
  borderRadius: {
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    full: "9999px"
  },
  
  // Shadows
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1)"
  },
  
  // Transitions
  transitions: {
    fast: "150ms ease-in-out",
    normal: "250ms ease-in-out",
    slow: "350ms ease-in-out"
  }
};

// Interface pour le thème complet
export interface BrandTheme extends BrandConfig {
  // Propriétés du thème de base
  fonts: typeof baseTheme.fonts;
  fontSizes: typeof baseTheme.fontSizes;
  spacing: typeof baseTheme.spacing;
  borderRadius: typeof baseTheme.borderRadius;
  shadows: typeof baseTheme.shadows;
  transitions: typeof baseTheme.transitions;
  
  // Propriétés calculées
  gradients: {
    primary: string;
    secondary: string;
    accent: string;
  };
  
  // Composants
  components: {
    button: {
      primary: {
        backgroundColor: string;
        color: string;
        fontSize: number;
        fontWeight: string;
      };
      secondary: {
        backgroundColor: string;
        color: string;
        fontSize: number;
        fontWeight: string;
      };
    };
    card: {
      backgroundColor: string;
      padding: string;
      borderRadius: string;
      boxShadow: string;
    };
  };
}

// Hook personnalisé pour utiliser le thème de la marque
export const useBrandTheme = (): BrandTheme => {
  const brandConfig = useMemo(() => getBrandConfig(), []);
  
  // Génération des gradients basés sur les couleurs de la marque
  const gradients = useMemo(() => ({
    primary: `linear-gradient(135deg, ${brandConfig.colors.primary}, ${brandConfig.colors.secondary})`,
    secondary: `linear-gradient(135deg, ${brandConfig.colors.secondary}, ${brandConfig.colors.dark})`,
    accent: `linear-gradient(135deg, ${brandConfig.colors.accent}, ${brandConfig.colors.warning})`
  }), [brandConfig.colors]);
  
  // Configuration des composants
  const components = useMemo(() => ({
    button: {
      primary: {
        backgroundColor: brandConfig.colors.primary,
        color: "white",
        fontSize: 20,
        fontWeight: "bold"
      },
      secondary: {
        backgroundColor: brandConfig.colors.secondary,
        color: "white",
        fontSize: 18,
        fontWeight: "600"
      }
    },
    card: {
      backgroundColor: "white",
      padding: "16px",
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
    }
  }), [brandConfig.colors]);
  
  // Fusion du thème de base avec la configuration de la marque
  const theme: BrandTheme = useMemo(() => ({
    ...brandConfig,
    ...baseTheme,
    gradients,
    components
  }), [brandConfig, gradients, components]);
  
  return theme;
};

// Hook pour obtenir uniquement la configuration de la marque
export const useBrandConfig = (): BrandConfig => {
  return useMemo(() => getBrandConfig(), []);
};

// Hook pour vérifier si une fonctionnalité est activée
export const useFeatureFlag = (feature: keyof BrandConfig['features']): boolean => {
  const brandConfig = useMemo(() => getBrandConfig(), []);
  return brandConfig.features[feature];
};

export default useBrandTheme;

